import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

const PAISES = [
  { iso: "CL", nombre: "Chile", codigo: "56" },
  { iso: "AR", nombre: "Argentina", codigo: "54" },
  { iso: "BO", nombre: "Bolivia", codigo: "591" },
  { iso: "BR", nombre: "Brasil", codigo: "55" },
  { iso: "CO", nombre: "Colombia", codigo: "57" },
  { iso: "CR", nombre: "Costa Rica", codigo: "506" },
  { iso: "CU", nombre: "Cuba", codigo: "53" },
  { iso: "EC", nombre: "Ecuador", codigo: "593" },
  { iso: "SV", nombre: "El Salvador", codigo: "503" },
  { iso: "GT", nombre: "Guatemala", codigo: "502" },
  { iso: "HN", nombre: "Honduras", codigo: "504" },
  { iso: "MX", nombre: "México", codigo: "52" },
  { iso: "NI", nombre: "Nicaragua", codigo: "505" },
  { iso: "PA", nombre: "Panamá", codigo: "507" },
  { iso: "PY", nombre: "Paraguay", codigo: "595" },
  { iso: "PE", nombre: "Perú", codigo: "51" },
  { iso: "UY", nombre: "Uruguay", codigo: "598" },
  { iso: "VE", nombre: "Venezuela", codigo: "58" },
  { iso: "US", nombre: "EE.UU. / Canadá (+1)", codigo: "1" },
  { iso: "ES", nombre: "España", codigo: "34" },
  { iso: "PT", nombre: "Portugal", codigo: "351" },
  { iso: "FR", nombre: "Francia", codigo: "33" },
  { iso: "DE", nombre: "Alemania", codigo: "49" },
  { iso: "IT", nombre: "Italia", codigo: "39" },
  { iso: "GB", nombre: "Reino Unido", codigo: "44" },
  { iso: "IE", nombre: "Irlanda", codigo: "353" },
  { iso: "NL", nombre: "Países Bajos", codigo: "31" },
  { iso: "BE", nombre: "Bélgica", codigo: "32" },
  { iso: "CH", nombre: "Suiza", codigo: "41" },
  { iso: "AT", nombre: "Austria", codigo: "43" },
  { iso: "SE", nombre: "Suecia", codigo: "46" },
  { iso: "NO", nombre: "Noruega", codigo: "47" },
  { iso: "DK", nombre: "Dinamarca", codigo: "45" },
  { iso: "FI", nombre: "Finlandia", codigo: "358" },
  { iso: "PL", nombre: "Polonia", codigo: "48" },
  { iso: "CZ", nombre: "Rep. Checa", codigo: "420" },
  { iso: "GR", nombre: "Grecia", codigo: "30" },
  { iso: "RO", nombre: "Rumania", codigo: "40" },
  { iso: "UA", nombre: "Ucrania", codigo: "380" },
  { iso: "RU", nombre: "Rusia", codigo: "7" },
  { iso: "TR", nombre: "Turquía", codigo: "90" },
  { iso: "IL", nombre: "Israel", codigo: "972" },
  { iso: "AE", nombre: "Emiratos Árabes", codigo: "971" },
  { iso: "SA", nombre: "Arabia Saudita", codigo: "966" },
  { iso: "IN", nombre: "India", codigo: "91" },
  { iso: "CN", nombre: "China", codigo: "86" },
  { iso: "JP", nombre: "Japón", codigo: "81" },
  { iso: "KR", nombre: "Corea del Sur", codigo: "82" },
  { iso: "AU", nombre: "Australia", codigo: "61" },
  { iso: "NZ", nombre: "Nueva Zelanda", codigo: "64" },
  { iso: "ZA", nombre: "Sudáfrica", codigo: "27" },
];

const flagFromIso = (iso) =>
  iso
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const findCountryAndNumber = (value, defaultCountryCode) => {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  const defaultCountry = PAISES.find((p) => p.codigo === defaultCountryCode) || PAISES[0];

  if (!digits) return { country: defaultCountry, number: "" };

  if (raw.startsWith("+")) {
    const matches = PAISES.filter((p) => digits.startsWith(p.codigo)).sort(
      (a, b) => b.codigo.length - a.codigo.length,
    );
    if (matches.length) {
      return {
        country: matches[0],
        number: digits.substring(matches[0].codigo.length),
      };
    }
  }

  if (digits.startsWith(defaultCountry.codigo) && digits.length > defaultCountry.codigo.length + 5) {
    return {
      country: defaultCountry,
      number: digits.substring(defaultCountry.codigo.length),
    };
  }

  return { country: defaultCountry, number: digits };
};

export const validarTelefonoInternacional = (value) => {
  if (!value) return true;
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const TelefonoInput = ({
  value,
  onChange,
  id = "telefono",
  className = "",
  placeholder = "Número de teléfono",
  disabled = false,
  invalid = false,
  defaultCountryCode = "56",
}) => {
  const parsed = useMemo(
    () => findCountryAndNumber(value, defaultCountryCode),
    [value, defaultCountryCode],
  );
  const [country, setCountry] = useState(parsed.country);
  const [number, setNumber] = useState(parsed.number);

  useEffect(() => {
    setCountry(parsed.country);
    setNumber(parsed.number);
  }, [parsed]);

  const emit = (nextCountry, nextNumber) => {
    const cleanNumber = String(nextNumber || "").replace(/\D/g, "");
    onChange?.(cleanNumber ? `+${nextCountry.codigo}${cleanNumber}` : "");
  };

  const selectedTemplate = (option) =>
    option ? `${flagFromIso(option.iso)} +${option.codigo}` : "País";
  const itemTemplate = (option) =>
    `${flagFromIso(option.iso)} ${option.nombre} (+${option.codigo})`;

  return (
    <div className={`flex gap-2 ${className}`.trim()}>
      <Dropdown
        value={country}
        options={PAISES}
        onChange={(e) => {
          const nextCountry = e.value;
          setCountry(nextCountry);
          emit(nextCountry, number);
        }}
        optionLabel="nombre"
        valueTemplate={selectedTemplate}
        itemTemplate={itemTemplate}
        filter
        filterBy="nombre,codigo"
        disabled={disabled}
        className="flex-shrink-0"
        style={{ width: "9.5rem" }}
      />
      <InputText
        id={id}
        value={number}
        onChange={(e) => {
          const maxLength = Math.max(4, 15 - country.codigo.length);
          const nextNumber = e.target.value.replace(/\D/g, "").slice(0, maxLength);
          setNumber(nextNumber);
          emit(country, nextNumber);
        }}
        placeholder={placeholder}
        inputMode="tel"
        disabled={disabled}
        className={`w-full ${invalid ? "p-invalid" : ""}`.trim()}
      />
    </div>
  );
};

export default TelefonoInput;
